import React, { useCallback, useState } from "react";
import "./ShopGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { readBestScore, recordScore, shopBestScoreKey } from "../Quiz/bestScore.ts";
import { track } from "../../Shared/analytics.ts";
import { ShopGameRun } from "./ShopGameRun.tsx";
import { ShopGameSummary } from "./ShopGameSummary.tsx";
import { PATIENCE_MS, SHOP_LEVELS, SHOP_PACES, ShopLevel, ShopPace, ShopResult } from "./shopGame.ts";
import { readShopLevel, saveShopLevel } from "./shopStore.ts";
import { shopGameEventParams } from "./gameEventParams.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

interface ShopGamePageProps {
    slug: string;
    /** The route's `quiz`: the operation the calmer quiz is found by, since the orders add and subtract money (ADR 0131 §5). */
    operation: Operations;
}

interface Summary {
    result: ShopResult;
    best: number | null;
    isNewBest: boolean;
}

/** What each grade's end screen sends the child to practise, by slug (ADR 0131 §7). */
export const PRACTICE: Record<ShopLevel, string[]> = {
    g2: ["/counting-money-worksheets", "/adding-money", "/identifying-fractions-worksheets"],
    g3: ["/making-change-worksheets", "/money-word-problems", "/multiplication-word-problems"],
    g4: ["/making-change-worksheets", "/money-word-problems", "/fraction-of-a-number-worksheets"],
    g5: ["/money-word-problems", "/fraction-of-a-number-worksheets", "/multiplication-word-problems"],
    g6: ["/percentage-of-a-number-worksheets", "/discount-and-markup-worksheets", "/unit-rate-worksheets"],
    g7: ["/sales-tax-and-tip-worksheets", "/discount-and-markup-worksheets", "/percent-word-problems"],
    g8: ["/percent-word-problems", "/simple-interest-worksheets", "/discount-and-markup-worksheets"],
};

/** Each level's and pace's name. `labelKey` so StringKeys.test.js sees the keys, as QuizPage's modes do. */
const LEVEL_LABELS: Record<ShopLevel, { labelKey: StringKey }> = {
    g2: { labelKey: "flashCards.shopLevelG2" },
    g3: { labelKey: "flashCards.shopLevelG3" },
    g4: { labelKey: "flashCards.shopLevelG4" },
    g5: { labelKey: "flashCards.shopLevelG5" },
    g6: { labelKey: "flashCards.shopLevelG6" },
    g7: { labelKey: "flashCards.shopLevelG7" },
    g8: { labelKey: "flashCards.shopLevelG8" },
};

const LEVEL_NOTES: Record<ShopLevel, { labelKey: StringKey }> = {
    g2: { labelKey: "flashCards.shopLevelNoteG2" },
    g3: { labelKey: "flashCards.shopLevelNoteG3" },
    g4: { labelKey: "flashCards.shopLevelNoteG4" },
    g5: { labelKey: "flashCards.shopLevelNoteG5" },
    g6: { labelKey: "flashCards.shopLevelNoteG6" },
    g7: { labelKey: "flashCards.shopLevelNoteG7" },
    g8: { labelKey: "flashCards.shopLevelNoteG8" },
};

const PACE_LABELS: Record<ShopPace, { labelKey: StringKey }> = {
    calm: { labelKey: "flashCards.shopPaceCalm" },
    busy: { labelKey: "flashCards.shopPaceBusy" },
    rush: { labelKey: "flashCards.shopPaceRush" },
};

const bestKeys = (): Record<string, number | null> => {
    const all: Record<string, number | null> = {};
    for (const level of SHOP_LEVELS) {
        for (const pace of SHOP_PACES) {
            const key = shopBestScoreKey(level, pace);
            all[key] = readBestScore(key);
        }
    }
    return all;
};

/**
 * Shop Rush (ADR 0131): a keyword landing page around a shift at the shop counter,
 * with a button for each grade, 2 to 8 (ADR 0131 §7), and a pace picker as Math
 * Runner has (ADR 0124). A quiz route carrying `game: "shop"`; its `quiz` is only the
 * operation the link to the calmer quiz is found by, since the game has no queue,
 * picker, settings or missed facts to wire.
 */
export function ShopGamePage({ slug, operation }: ShopGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [level, setLevel] = useState<ShopLevel>(readShopLevel);
    const [pace, setPace] = useState<ShopPace>("calm");
    const [playing, setPlaying] = useState(false);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [bests, setBests] = useState<Record<string, number | null>>(bestKeys);

    const plainQuiz = quizRoutes().find((route) => route.quiz === operation && !route.game);
    const best = bests[shopBestScoreKey(level, pace)] ?? null;

    function start() {
        setSummary(null);
        setPlaying(true);
        setRunId((id) => id + 1);
    }

    function chooseLevel(next: ShopLevel) {
        if (next === level) {
            return;
        }
        setLevel(next);
        saveShopLevel(next);
        setSummary(null);
    }

    function choosePace(next: ShopPace) {
        if (next === pace) {
            return;
        }
        setPace(next);
        setSummary(null);
    }

    const handleFinish = useCallback((result: ShopResult) => {
        const key = shopBestScoreKey(result.level, result.pace);
        const previous = readBestScore(key);
        const recorded = recordScore(key, result.stars);
        // After the store, as the quiz does: analytics never costs a best score.
        track("quiz_completed", shopGameEventParams(result));
        setBests((all) => ({ ...all, [key]: recorded > 0 ? recorded : null }));
        setSummary({
            result,
            best: recorded > 0 ? recorded : null,
            isNewBest: previous !== null ? result.stars > previous : result.stars > 0,
        });
        setPlaying(false);
    }, []);

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="shopTool">
                {playing ? (
                    <ShopGameRun key={runId} level={level} pace={pace} onFinish={handleFinish} />
                ) : (
                    <>
                        {summary && (
                            <ShopGameSummary
                                result={summary.result}
                                best={summary.best}
                                isNewBest={summary.isNewBest}
                                practiceSlugs={PRACTICE[summary.result.level]}
                                onPlayAgain={() => {
                                    setLevel(summary.result.level);
                                    setPace(summary.result.pace);
                                    start();
                                }}
                            />
                        )}

                        <p className="shopHow">{t("flashCards.shopHowToPlay")}</p>

                        <div className="shopGroup shopGroup--levels" role="group" aria-label={t("flashCards.shopLevelsLabel")}>
                            {SHOP_LEVELS.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    className="shopChoice"
                                    aria-pressed={option === level}
                                    onClick={() => chooseLevel(option)}
                                >
                                    {t(LEVEL_LABELS[option].labelKey)}
                                </button>
                            ))}
                        </div>
                        <p className="shopLevelNote">{t(LEVEL_NOTES[level].labelKey)}</p>

                        <div className="shopGroup shopGroup--paces" role="group" aria-label={t("flashCards.shopPacesLabel")}>
                            {SHOP_PACES.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    className="shopChoice"
                                    aria-pressed={option === pace}
                                    onClick={() => choosePace(option)}
                                >
                                    <span>{t(PACE_LABELS[option].labelKey)}</span>
                                    <span className="shopChoiceNote">
                                        {PATIENCE_MS[option] === null
                                            ? t("flashCards.shopPaceNoClock")
                                            : format(t("flashCards.shopPaceSecondsTemplate"), {
                                                  n: PATIENCE_MS[option]! / 1000,
                                              })}
                                    </span>
                                </button>
                            ))}
                        </div>

                        <p className="shopBest">{best !== null ? format(t("flashCards.yourBestTemplate"), { n: best }) : ""}</p>

                        <button type="button" className="btn btn-primary shopStart" onClick={start}>
                            {t("flashCards.shopStart")}
                        </button>

                        {plainQuiz && (
                            <p className="shopCalmer">
                                {t("flashCards.shopCalmerPrefix")}
                                <a href={plainQuiz.slug}>{t("flashCards.shopCalmerLink")}</a>
                                {t("flashCards.shopCalmerSuffix")}
                            </p>
                        )}
                    </>
                )}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default ShopGamePage;
